import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Row, Spin, Typography } from 'antd'
import { isEmpty, isNil } from 'lodash'
import PropTypes from 'prop-types'

import { DirtyPrompt } from '../../../core/components/sections'

import { PermissionGuard } from '../../../common/components/routes'
import {
  FormButtons,
  IngredientKeywordTable,
  TermErrors,
} from '../../../common/components/sections'
import { StyledVerticalSpace } from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import { useUserHasPermissions } from '../../../common/hooks'
import { idPropType } from '../../../common/prop-types'

import {
  useIngredientKeywordsQuery,
  useTransferIngredientKeywordsToDictionaryTypeMutation,
  useUpdateIngredientKeywordDetailsMutation,
  useUpdateTransferedKeywordsMutation,
} from '../../hooks'
import {
  addKeywordNode,
  checkMainKeywordNodesForType,
  checkSupportingKeywordNodesForType,
  clearMainKeywordError,
  clearTransferKeywordError,
  renameKeywordNode,
  resetIngredienKeywords,
  setDataForRenamingKeyword,
  setIngredientPageIsDirty,
} from '../../redux/actions'
import { IngredientKeywordActionButtons } from '../sections'

const IngredientKeywords = ({
  ingredientId,
  ingredientOptions,
  languageOptions,
}) => {
  const dispatch = useDispatch()
  const [isMainTableCheckable, setIsMainTableCheckable] = useState(false)
  const [isSupportingTableShown, setIsSupportingTableShown] = useState(false)

  const [
    fetchIngredientKeywordsById,
    { isLoading: isFetchLoading, error: fetchError },
  ] = useIngredientKeywordsQuery(ingredientId, true)

  const [
    updateIngredientKeywordDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateIngredientKeywordDetailsMutation()

  const [
    updateTransferedKeywords,
    { isLoading: isTransferLoading, error: transferError },
  ] = useUpdateTransferedKeywordsMutation()

  const [transferIngredientKeywordsToDictionaryType] =
    useTransferIngredientKeywordsToDictionaryTypeMutation()

  const {
    isDirty,
    dataForRenaming,
    data: mainKeywordsData,
    checkedKeywordKeys: mainCheckedKeywordKeysGroupedByType,
  } = useSelector(store => store.ingredients.ingredient?.keywords?.main || {})

  const {
    data: supportingKeywordsData,
    checkedKeywordKeys: supportingCheckedKeywordKeysGroupedByType,
  } = useSelector(
    store => store.ingredients.ingredient?.keywords?.supporting || {},
  )

  const hasFullPermission = useUserHasPermissions([
    PERMISSIONS.INGREDIENT.SAVE_KEYWORDS,
  ])

  const handleOnAddNode = useCallback(
    (node, field) => dispatch(addKeywordNode({ node, field })),
    [dispatch],
  )

  const handleOnRenameNode = useCallback(
    ({ path, term }, field) =>
      dispatch(renameKeywordNode({ path, term, field })),
    [dispatch],
  )

  const handleOnSetDataForRenaming = useCallback(
    id => dispatch(setDataForRenamingKeyword(id)),
    [dispatch],
  )

  const handleOnCheckMain = useCallback(
    (checkedKeys, type) =>
      dispatch(checkMainKeywordNodesForType({ checkedKeys, type })),
    [dispatch],
  )

  const handleOnCheckSupporting = useCallback(
    (checkedKeys, type) =>
      dispatch(checkSupportingKeywordNodesForType({ checkedKeys, type })),
    [dispatch],
  )

  const handleResetKeywords = useCallback(
    () => dispatch(resetIngredienKeywords()),
    [dispatch],
  )

  const handleSaveData = useCallback(
    () => updateIngredientKeywordDetails(ingredientId, mainKeywordsData),
    [ingredientId, mainKeywordsData, updateIngredientKeywordDetails],
  )

  useEffect(() => {
    if (saveError?.message || fetchError || transferError) {
      showErrorNotification({
        title: 'Ingredient details error',
        description: saveError?.message || fetchError || transferError,
      })

      if (isEmpty(saveError?.duplicated)) {
        dispatch(clearMainKeywordError())
        dispatch(clearTransferKeywordError())
      }
    }
  }, [
    dispatch,
    fetchError,
    saveError?.message,
    saveError?.duplicated,
    transferError,
  ])

  useEffect(() => {
    dispatch(setIngredientPageIsDirty({ isKeywordsDirty: isDirty }))
  }, [dispatch, isDirty])

  const { duplicated } = saveError || {}

  return isSaveLoading || isTransferLoading || isNil(mainKeywordsData) ? (
    <Spin />
  ) : (
    <>
      <DirtyPrompt isDirty={isDirty} />
      <Typography.Title level={3}>
        Related Terms Categorisation
      </Typography.Title>
      <StyledVerticalSpace>
        <PermissionGuard permissions={PERMISSIONS.INGREDIENT.SAVE_KEYWORDS}>
          <IngredientKeywordActionButtons
            ingredientId={ingredientId}
            isDirty={isDirty}
            fetchIngredientKeywordsById={fetchIngredientKeywordsById}
            ingredientOptions={ingredientOptions}
            languageOptions={languageOptions}
            mainCheckedKeywordKeysGroupedByType={
              mainCheckedKeywordKeysGroupedByType
            }
            supportingCheckedKeywordKeysGroupedByType={
              supportingCheckedKeywordKeysGroupedByType
            }
            updateTransferedKeywords={updateTransferedKeywords}
            transferIngredientKeywordsToDictionaryType={
              transferIngredientKeywordsToDictionaryType
            }
            setIsMainTableCheckable={setIsMainTableCheckable}
            setIsSupportingTableShown={setIsSupportingTableShown}
          />
        </PermissionGuard>
        {!isEmpty(duplicated) && <TermErrors errors={duplicated} />}
        <IngredientKeywordTable
          data={mainKeywordsData}
          onAdd={handleOnAddNode}
          onRename={handleOnRenameNode}
          setDataForRenaming={handleOnSetDataForRenaming}
          dataForRenaming={dataForRenaming}
          onCheck={handleOnCheckMain}
          editable={hasFullPermission && !isMainTableCheckable}
          checkable={isMainTableCheckable}
          checkedKeywordNodesGroupedByType={mainCheckedKeywordKeysGroupedByType}
          languageOptions={languageOptions}
        />

        {isSupportingTableShown && (
          <>
            {isFetchLoading ? (
              <Spin />
            ) : (
              <>
                <Typography.Title level={3}>
                  Transfer (select one item)
                </Typography.Title>

                <IngredientKeywordTable
                  data={supportingKeywordsData}
                  onCheck={handleOnCheckSupporting}
                  editable={false}
                  checkable
                  checkStrictly
                  checkedKeywordNodesGroupedByType={
                    supportingCheckedKeywordKeysGroupedByType
                  }
                  languageOptions={languageOptions}
                />
              </>
            )}
          </>
        )}

        <Row>
          <FormButtons
            permissions={[PERMISSIONS.INGREDIENT.SAVE_KEYWORDS]}
            isDisabled={!isDirty}
            onReset={handleResetKeywords}
            onSave={handleSaveData}
          />
        </Row>
      </StyledVerticalSpace>
    </>
  )
}

IngredientKeywords.propTypes = {
  ingredientId: idPropType.isRequired,
  ingredientOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  languageOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
}

export default IngredientKeywords
